import React, { useEffect } from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useLanguage } from '../i18n/LanguageContext';
import { LEGAL_DOCUMENTS, type LegalDocId } from '../data/legalDocuments';
export function LegalDocumentPage({
  docId
}: {
  docId: LegalDocId;
}) {
  const {
    lang
  } = useLanguage();
  const doc = LEGAL_DOCUMENTS[docId];
  // Legal docs are authored in RU + KK only; fall back to RU for other locales.
  const content = doc[lang as 'ru' | 'kk'] || doc.ru;
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'auto'
    });
  }, [docId]);
  return <div className="min-h-screen bg-[#FAF7F2]" data-id="element-390">
      <Navbar data-id="element-391" />

      {/* Decorative top gradient */}
      <div className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none" style={{
      background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(124,77,255,0.10) 0%, transparent 70%)'
    }} data-id="element-392" />

      <main className="relative pt-28 pb-20 px-4 sm:px-6 lg:px-8" data-id="element-393">
        <div className="max-w-3xl mx-auto" data-id="element-394">
          {/* Back link */}
          <a href="#" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#7C4DFF] hover:text-[#651FFF] transition-colors mb-8" data-id="element-395">
            <ArrowLeftIcon size={15} strokeWidth={2.2} data-id="element-396" />
            {content.backToHome.replace('← ', '')}
          </a>

          {/* Header */}
          <header className="mb-12" data-id="element-397">
            <span className="inline-block text-[11px] font-bold tracking-[0.18em] uppercase text-[#7C4DFF] bg-[#EDE7F6] px-3 py-1.5 rounded-full mb-5" data-id="element-398">
              {content.badge}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-[42px] font-bold text-gray-900 leading-[1.15] tracking-tight mb-4" data-id="element-399">
              {content.title}
            </h1>
            {content.updated && <p className="text-sm text-gray-500 font-medium" data-id="element-400">
                {content.updated}
              </p>}
          </header>

          {/* Document card */}
          <article className="bg-white rounded-3xl shadow-sm border border-gray-100 p-7 sm:p-10 lg:p-14" data-id="element-401">
            {/* Intro */}
            {content.intro.length > 0 && <div className="space-y-4 mb-10" data-id="element-402">
                {content.intro.map((p, i) => <p key={i} className="text-[15px] sm:text-base text-gray-700 leading-relaxed" data-id="element-403">
                    {p}
                  </p>)}
                {content.agreement && <div className="mt-6 p-4 rounded-2xl border-l-4 border-[#7C4DFF] bg-[#F5F3FF]" data-id="element-404">
                    <p className="text-[15px] text-[#5B21B6] font-semibold leading-relaxed" data-id="element-405">
                      {content.agreement}
                    </p>
                  </div>}
              </div>}

            {/* Sections */}
            <div className="space-y-12" data-id="element-406">
              {content.sections.map((section, si) => <section key={si} className="scroll-mt-28" data-id="element-407">
                  <div className="flex items-baseline gap-3 mb-4" data-id="element-408">
                    {section.number && <span className="text-xs font-bold text-[#7C4DFF] tracking-widest" data-id="element-409">
                        {section.number}
                      </span>}
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight" data-id="element-410">
                      {section.title}
                    </h2>
                  </div>

                  <div className="h-px bg-gray-100 mb-5" data-id="element-411" />

                  {section.intro && <p className="text-[15px] text-gray-700 leading-relaxed mb-4" data-id="element-412">
                      {section.intro}
                    </p>}

                  {section.body && section.body.map((p, i) => <p key={i} className="text-[15px] text-gray-700 leading-relaxed mb-3" data-id="element-413">
                        {p}
                      </p>)}

                  {section.groups && section.groups.map((group, gi) => <div key={gi} className={gi > 0 ? 'mt-5' : 'mt-2'} data-id="element-414">
                        {group.heading && <p className="text-sm font-semibold text-gray-900 mb-2.5" data-id="element-415">
                            {group.heading}
                          </p>}
                        <ul className="space-y-2" data-id="element-416">
                          {group.items.map((item, ii) => <li key={ii} className="flex items-start gap-3 text-[15px] text-gray-700 leading-relaxed" data-id="element-417">
                              <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-[#7C4DFF]" aria-hidden="true" data-id="element-418" />
                              <span data-id="element-419">{item}</span>
                            </li>)}
                        </ul>
                      </div>)}

                  {section.outro && <p className="text-[15px] text-gray-700 leading-relaxed mt-4" data-id="element-420">
                      {section.outro}
                    </p>}
                </section>)}
            </div>
          </article>
        </div>
      </main>

      <Footer data-id="element-421" />
    </div>;
}